import { CHANNEL, EMAILS_CHANNELS } from 'src/data/channel';
import { COPY_FIELD, getFilterAssetType } from 'src/data/performance';
import { formatDateMedNonLocale } from 'src/lib/format';
import { formatNumber, formatPercentage } from 'src/utils/format';

export const ROWS_PER_PAGE_OPTIONS = [25, 50, 100];
export const ROWS_PER_PAGE_DEFAULT = ROWS_PER_PAGE_OPTIONS[0];

const ADS_TABLE_COLUMNS = [
  {
    field: COPY_FIELD.boost,
    label: '',
    align: 'left',
    style: {
      position: 'sticky',
      minWidth: 122,
      left: 0,
      zIndex: 3,
    },
  },
  {
    field: COPY_FIELD.text,
    label: 'Copy',
    align: 'left',
    style: {
      minWidth: 400,
    },
  },
  {
    field: COPY_FIELD.campaignNames,
    label: 'Campaigns',
    align: 'left',
    style: { minWidth: 220 },
    format: (value) => (value || []).join(', '),
  },
  {
    field: COPY_FIELD.textType,
    label: 'Asset type',
    align: 'left',
    style: { minWidth: 120 },
    format: (value, channel) => getFilterAssetType(value, channel),
  },
  {
    field: COPY_FIELD.keywords,
    label: 'Top keywords',
    align: 'left',
    style: { minWidth: 240 },
    format: (value) => (value || []).join(', '),
    getHint: () => 'These Keywords appeared in the highest spending campaigns that ran this copy',
  },
  {
    field: COPY_FIELD.sourceAnyword,
    label: 'Source',
    align: 'left',
    style: { minWidth: 80 },
    format: (value) => (value ? 'Anyword' : ''),
  },
  {
    field: COPY_FIELD.numberOfInstances,
    label: '# Instances (Ads)',
    align: 'left',
    style: { minWidth: 136 },
    format: (value) => formatNumber(value),
  },
  {
    field: COPY_FIELD.impressions,
    label: 'Total impressions',
    align: 'left',
    style: { minWidth: 120 },
    format: (value) => formatNumber(value),
  },
  {
    field: COPY_FIELD.weightedCtr,
    label: 'CTR',
    align: 'left',
    style: { minWidth: 200 },
    format: (value) => formatPercentage(value),
  },
  {
    field: COPY_FIELD.startDate,
    label: 'First spend',
    align: 'left',
    style: { minWidth: 102 },
    format: (value) => formatDateMedNonLocale(value),
  },
];

const EMAILS_TABLE_COLUMNS = [
  {
    field: COPY_FIELD.boost,
    label: '',
    align: 'left',
    style: {
      minWidth: 122,
    },
  },
  {
    field: COPY_FIELD.text,
    label: 'Copy',
    align: 'left',
    style: {
      minWidth: 400,
    },
  },
  {
    field: COPY_FIELD.campaignNames,
    label: 'Campaigns',
    align: 'left',
    style: { minWidth: 220 },
    format: (value) => (value || []).join(', '),
  },
  {
    field: COPY_FIELD.textType,
    label: 'Asset type',
    align: 'left',
    style: { minWidth: 120 },
    format: (value, channel) => getFilterAssetType(value, channel),
  },
  {
    field: COPY_FIELD.opens,
    label: 'Open rate',
    align: 'left',
    style: { minWidth: 200 },
  },
  {
    field: COPY_FIELD.weightedCtr,
    label: 'CTR',
    align: 'left',
    style: { minWidth: 200 },
    format: (value) => formatPercentage(value),
  },
  {
    field: COPY_FIELD.impressions,
    label: 'Delivered',
    align: 'left',
    style: { minWidth: 120 },
    format: (value) => formatNumber(value),
  },
  {
    field: COPY_FIELD.startDate,
    label: 'First send',
    align: 'left',
    style: { minWidth: 102 },
    format: (value) => formatDateMedNonLocale(value),
  },
];

const FACEBOOK_PAGES_TABLE_COLUMNS = [
  {
    field: COPY_FIELD.boost,
    label: '',
    align: 'left',
    style: {
      minWidth: 122,
    },
  },
  {
    field: COPY_FIELD.text,
    label: 'Copy',
    align: 'left',
    style: {
      minWidth: 400,
    },
  },
  {
    field: COPY_FIELD.impressions,
    label: 'Reach',
    align: 'left',
    style: { minWidth: 149 },
  },
  {
    field: COPY_FIELD.reactions,
    label: 'Likes and reactions',
    align: 'left',
    style: { minWidth: 129 },
  },
  {
    field: COPY_FIELD.clicks,
    label: 'Link clicks',
    align: 'left',
    style: { minWidth: 114 },
  },
  {
    field: COPY_FIELD.shares,
    label: 'Shares',
    align: 'left',
    style: { minWidth: 149 },
  },
  {
    field: COPY_FIELD.sourceAnyword,
    label: 'Source',
    align: 'left',
    style: { minWidth: 80 },
    format: (value) => (value ? 'Anyword' : ''),
  },
  {
    field: COPY_FIELD.startDate,
    label: 'First spend',
    align: 'left',
    style: { minWidth: 102 },
    format: (value) => formatDateMedNonLocale(value),
  },
];

const INSTAGRAM_TABLE_COLUMNS = [
  {
    field: COPY_FIELD.boost,
    label: '',
    align: 'left',
    style: {
      minWidth: 122,
    },
  },
  {
    field: COPY_FIELD.text,
    label: 'Copy',
    align: 'left',
    style: {
      minWidth: 400,
    },
  },
  {
    field: COPY_FIELD.impressions,
    label: 'Reach',
    align: 'left',
    style: { minWidth: 149 },
  },
  {
    field: COPY_FIELD.likes,
    label: 'Likes',
    align: 'left',
    style: { minWidth: 129 },
  },
  {
    field: COPY_FIELD.engagement,
    label: 'Engagement',
    align: 'left',
    style: { minWidth: 114 },
  },
  {
    field: COPY_FIELD.comments,
    label: 'Comments',
    align: 'left',
    style: { minWidth: 114 },
  },
  {
    field: COPY_FIELD.sourceAnyword,
    label: 'Source',
    align: 'left',
    style: { minWidth: 80 },
    format: (value) => (value ? 'Anyword' : ''),
  },
  {
    field: COPY_FIELD.startDate,
    label: 'First spend',
    align: 'left',
    style: { minWidth: 102 },
    format: (value) => formatDateMedNonLocale(value),
  },
];

const LINKEDIN_SOCIAL_TABLE_COLUMNS = [
  {
    field: COPY_FIELD.boost,
    label: '',
    align: 'left',
    style: {
      minWidth: 122,
    },
  },
  {
    field: COPY_FIELD.text,
    label: 'Copy',
    align: 'left',
    style: {
      minWidth: 400,
    },
  },
  {
    field: COPY_FIELD.impressions,
    label: 'Reach',
    align: 'left',
    style: { minWidth: 149 },
  },
  {
    field: COPY_FIELD.weightedCtr,
    label: 'CTR',
    align: 'left',
    style: { minWidth: 200 },
    format: (value) => formatPercentage(value),
  },
  {
    field: COPY_FIELD.sourceAnyword,
    label: 'Source',
    align: 'left',
    style: { minWidth: 80 },
    format: (value) => (value ? 'Anyword' : ''),
  },
  {
    field: COPY_FIELD.startDate,
    label: 'First spend',
    align: 'left',
    style: { minWidth: 102 },
    format: (value) => formatDateMedNonLocale(value),
  },
];

const TWITTER_SOCIAL_TABLE_COLUMNS = [
  {
    field: COPY_FIELD.boost,
    label: '',
    align: 'left',
    style: {
      minWidth: 122,
    },
  },
  {
    field: COPY_FIELD.text,
    label: 'Copy',
    align: 'left',
    style: {
      minWidth: 400,
    },
  },
  {
    field: COPY_FIELD.impressionCount,
    label: 'Impression',
    align: 'left',
    style: { minWidth: 149 },
  },
  {
    field: COPY_FIELD.likesCount,
    label: 'Likes',
    align: 'left',
    style: { minWidth: 129 },
  },
  {
    field: COPY_FIELD.quoteCount,
    label: 'Quotes',
    align: 'left',
    style: { minWidth: 114 },
  },
  {
    field: COPY_FIELD.replyCount,
    label: 'Replays',
    align: 'left',
    style: { minWidth: 114 },
  },
  {
    field: COPY_FIELD.retweetCount,
    label: 'Reposts',
    align: 'left',
    style: { minWidth: 114 },
  },
  {
    field: COPY_FIELD.bookmarkCount,
    label: 'Bookmarks',
    align: 'left',
    style: { minWidth: 114 },
  },
  {
    field: COPY_FIELD.sourceAnyword,
    label: 'Source',
    align: 'left',
    style: { minWidth: 80 },
    format: (value) => (value ? 'Anyword' : ''),
  },
  {
    field: COPY_FIELD.startDate,
    label: 'First spend',
    align: 'left',
    style: { minWidth: 102 },
    format: (value) => formatDateMedNonLocale(value),
  },
];

export const prepareTableColumns = (channel) => {
  if ([CHANNEL.salesforce, CHANNEL.marketo, CHANNEL.salesforceExactTarget].includes(channel)) {
    return EMAILS_TABLE_COLUMNS.filter((col) => col.field !== COPY_FIELD.campaignNames);
  }
  if (EMAILS_CHANNELS.includes(channel) || channel === CHANNEL.eloqua) {
    return EMAILS_TABLE_COLUMNS;
  }
  if (channel === CHANNEL.facebookPages) {
    return FACEBOOK_PAGES_TABLE_COLUMNS;
  }
  if (channel === CHANNEL.instagram) {
    return INSTAGRAM_TABLE_COLUMNS;
  }
  if (channel === CHANNEL.linkedinSocial) {
    return LINKEDIN_SOCIAL_TABLE_COLUMNS;
  }
  if (channel === CHANNEL.google) {
    return ADS_TABLE_COLUMNS.filter((col) => col.field !== COPY_FIELD.weightedCtr);
  }
  if (channel === CHANNEL.twitterSocial) {
    return TWITTER_SOCIAL_TABLE_COLUMNS;
  }
  return ADS_TABLE_COLUMNS.filter((col) => col.field !== COPY_FIELD.keywords);
};
